import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_ACCESS_KEYS_RESOURCE } from 'src/features/accessKey/api/useListAccessKeys';
import { useUpdateAccessKey } from 'src/features/accessKey/api/useUpdateAccessKey';
import AccessKeyFormModal from 'src/features/accessKey/components/form/AccessKeyFormModal';
import { ACCESS_KEY_TYPE_TRANSLATION_MAP } from 'src/features/accessKey/utils/constants';
import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { AccessKey } from 'src/features/accessKey/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateAccessKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
    selectedAccessKey?: AccessKey;
}

function UpdateAccessKeyFormModal<TData>({
    className,
    selectedAccessKey,
    children,
}: UpdateAccessKeyFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateAccessKey({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCESS_KEYS_RESOURCE] });
            toast({
                title: formatMessage(accessKeyMessages.updateAccessKeySuccess),
                description: formatMessage(accessKeyMessages.updateAccessKeySuccessDescription, {
                    entry: selectedAccessKey
                        ? formatMessage(ACCESS_KEY_TYPE_TRANSLATION_MAP[selectedAccessKey.type])
                        : '-',
                }),
            });
        },
    });

    return (
        <AccessKeyFormModal
            selectedAccessKey={selectedAccessKey}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ id: selectedAccessKey?.id || '', parkingId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(accessKeyMessages.editAccessKey)}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(accessKeyMessages.editAccessKeySubtitle, {
                entry: selectedAccessKey ? formatMessage(ACCESS_KEY_TYPE_TRANSLATION_MAP[selectedAccessKey.type]) : '-',
            })}
        >
            {children}
        </AccessKeyFormModal>
    );
}

export default UpdateAccessKeyFormModal;
